/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import BaseSocial from 'scripts/social/base';

window.ga = window.ga || function () {
    ga.q = ga.q || [];
    ga.q.push(arguments);
};

ga.l = Date.now();

export var GoogleAnalytics = BaseSocial.extend({

    // by default any page view will be sent after init
    delayTracking: true,

    waitForTrack: false,

    trackerCreated: false,

    gaFieldsMap: {},

    cachedProperties: {},

    initialize: function (options) {
        this.apiUrl = this.appConfig.urls.api.ga.apiUrl;
        this.delayTracking = options.delayTracking ? true : false;
        this.gaFieldsMap = options.gaFieldsMap || this.gaFieldsMap;

        this._super(options);

        if (!this.options.delayTracking && this.waitForTrack) {
            this.waitForTrack = false;
            this.trackPage();
        }
    },

    isApiAvailable: function () {
        return this.apiLoaded;
    },

    onApiLoaded: function () {
        this._super();

        if (this.options.trackLinkClick) {
            this.$('body').on('click', function (event) {
                var counter = this.options.trackingLinkLevel,
                    className = this.options.trackingLinkClass,
                    target = this.$(event.target);

                while (counter) {
                    if (target.hasClass(className)) {
                        this.onLinkClick(target, event);
                        break;
                    }
                    target = target.parent();
                    counter--;
                }
            }.bind(this));
        }
    },

    /*
     * To track link click add class 'ga-tracking-link' and data-attributes:
     * data-category
     * data-action
     * data-label
     */
    onLinkClick: function ($el, event) {
        var url = $el.attr('href'),
            target = $el.attr('target'),

            category = $el.data('category'),
            action = $el.data('action'),
            label = $el.data('label');

        if (target !== '_blank') {
            event.preventDefault();
        }

        this.trackEvent(category, action, label, function () {
            this.goToURL(url, target);
        }.bind(this));
    },

    goToURL: function (url, target) {
        if (url && target !== '_blank') {
            window.location.href = url;
        }
    },

    cacheProperty: function (key, value) {
        this.cachedProperties[key] = value;
    },

    /*
     * Cache properties to update tracker when created or before send pageview
     * @param {object} fieldName
     * @param {string} fieldName
     * @param {string} fieldValue
     */
    setProperty: function (fieldName, fieldValue) {
        if (typeof fieldName === 'object') {
            this._.forOwn(fieldName, function (value, key) {
                this.cacheProperty(key, value);
            }.bind(this));
        } else {
            this.cacheProperty(fieldName, fieldValue);
        }
    },

    createTracker: function () {
        window.ga('create', window.globalSettings.analytics.trackingId, 'auto');
        this.trackerCreated = true;
    },

    /*
     * Set properties from cache to tracker and clear cache
     */
    updateTracker: function () {
        if (!this.cachedProperties.page) {
            this.cachedProperties.page = location.pathname;
        }

        this._.forOwn(this.cachedProperties, function (value, key) {
            var prop = this.gaFieldsMap[key] || key;
            window.ga('set', prop, value);
            delete this.cachedProperties[key];
        }.bind(this));
    },

    resumeTracking: function () {
        this.delayTracking = false;
        if (this.waitForTrack) {
            this.waitForTrack = false;
            this.trackPage();
        }
    },

    /*
     * Create tracker if doesn't exist, set properties and send pageview
     */
    trackPage: function () {
        if (!this.trackerCreated) {
            this.createTracker();
        }

        if (this.delayTracking) {
            this.waitForTrack = true;
            return;
        }

        this.updateTracker();

        window.ga('send', 'pageview');
    },

    /*
     * Send event
     * @param category
     * @param action
     * @param label
     * @param callback - to call when info is sent
     * @param {object} data - additional fields to send (optional)
     */
    trackEvent: function (category, action, label, callback, data, value) {
        var eventData = {
            hitType: 'event',
            page: location.pathname,
            eventCategory: category,
            eventAction: action,
            eventLabel: label,
            eventValue: value
        };

        if (callback && typeof callback === 'function') {
            eventData.hitCallback = callback;
        }

        if (data) {
            this._.forOwn(data, function (val, key) {
                var prop = this.gaFieldsMap[key] || key;
                eventData[prop] = val;
            }.bind(this));
        }

        this.updateTracker();
        window.ga('send', eventData);
    }

}, {});
